import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "@assets/icons/Icon";

type Side = "top" | "right" | "bottom" | "left";

interface HelpTooltipProps {
  content: ReactNode;
  side?: Side;
  size?: number;
}

export default function HelpTooltip({
  content,
  side = "top",
  size = 14,
}: HelpTooltipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const gap = 8;
    let top = 0;
    let left = 0;
    switch (side) {
      case "top":
        top = rect.top - gap;
        left = rect.left + rect.width / 2;
        break;
      case "bottom":
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2;
        left = rect.left - gap;
        break;
      case "right":
        top = rect.top + rect.height / 2;
        left = rect.right + gap;
        break;
    }
    setPos({ top, left });
  }, [open, side]);

  const transform = {
    top: "translate(-50%, -100%)",
    bottom: "translate(-50%, 0)",
    left: "translate(-100%, -50%)",
    right: "translate(0, -50%)",
  }[side];

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex items-center justify-center cursor-help text-neutral-400 hover:text-neutral-600"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        aria-label="Aide"
      >
        <Icon name="question" size={size} />
      </span>
      {open &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              transform,
              zIndex: 10000,
              maxWidth: 280,
              pointerEvents: "none",
            }}
            className="bg-neutral-800 text-white text-h5 rounded-md px-3 py-2 shadow-lg"
            role="tooltip"
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
